/**
 * @file     motion.ts
 * @purpose  Motion presets on the token scale: transition strings, press and photo-zoom styles, the CSS custom properties CSS modules read, and the below-the-fold reveal test. The global reduced-motion rule lives in src/app/globals.css.
 */
import { motionTokens } from '@/design-system/tokens/motion';

export type MotionDuration = 'instant' | 'fast' | 'base' | 'slow';
export type MotionEasing = keyof typeof motionTokens.easing;

const { durations, easing, interaction } = motionTokens;

export const motionTransition = (
  properties: string | readonly string[],
  duration: MotionDuration = 'fast',
  curve: MotionEasing = 'standard',
  delayMs = 0,
): string => {
  const list = typeof properties === 'string' ? [properties] : properties;
  const delay = delayMs > 0 ? ` ${delayMs}ms` : '';
  return list.map((p) => `${p} ${durations[duration]}ms ${easing[curve]}${delay}`).join(', ');
};

/** Hover, press and focus feedback for any control. `scale` is the press channel, so a positioned element's own transform is never overwritten. */
export const interactiveTransition = motionTransition(
  ['color', 'background-color', 'border-color', 'box-shadow', 'opacity', 'scale', 'transform'],
  'fast',
);

const PRESS_SELECTOR = '&:active:not(:disabled):not(.Mui-disabled)';

export const pressSx = (scale: number = interaction.pressScale) => ({
  [PRESS_SELECTOR]: { scale: String(scale) },
});

/** Card photo zoom: slow and calm, only on devices that really hover. */
export const photoZoom = {
  transition: motionTransition('transform', 'slow'),
  transform: `scale(${interaction.photoZoom})`,
} as const;

/** Mirrored into globals.css `:root`; a test keeps the two in step. */
export const motionCssVars: Record<string, string> = {
  '--motion-instant': `${durations.instant}ms`,
  '--motion-fast': `${durations.fast}ms`,
  '--motion-base': `${durations.base}ms`,
  '--motion-slow': `${durations.slow}ms`,
  '--ease-standard': easing.standard,
  '--ease-decelerate': easing.decelerate,
  '--ease-accelerate': easing.accelerate,
  '--ease-emphasized': easing.emphasized,
  '--ease-spring': easing.spring,
};

/**
 * Entrance reveals may only hide content the reader cannot see yet. Server
 * HTML always renders visible; after hydration an element is armed (hidden,
 * then revealed on scroll) only when it sits wholly below the viewport.
 */
export const shouldArmReveal = (rectTop: number, viewportHeight: number): boolean =>
  viewportHeight > 0 && rectTop >= viewportHeight;
